import { useContext, useState } from "react";
import { Badge, Button } from "flowbite-react";
import { ResponseContext } from "../../context/responseProvider";
import { StockBadge } from "../../helpers/merch-manager/helper";
import { ImagePlaceholder } from "../icons";
import { markPickedUp } from "../../services/merchManager/merchManagerActions";

const AcceptedRequestDetail = ({ request, merch, handleApi }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { setResponse, getResponse } = useContext(ResponseContext);
  const response = getResponse("mark_picked_up");
  const item = merch.find((m) => m.id === request.item_id) ?? null;
  const badge = item ? StockBadge(item.item_count) : null;

  const handlePickedUp = () => {
    setIsUpdating(true);
    markPickedUp({
      requestId: request.id,
      pickedUpBy: localStorage.getItem("userEmail"),
      setIsLoading: setIsUpdating,
      setResponse,
      onSuccess: () => handleApi(),
    });
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* ── Header ── */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
            {request.item_name}
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">
            by {request.requester_name ?? request.requested_by}
          </p>
        </div>

        <div>
          <div className="flex justify-end items-center gap-2">
            {request.status === "picked_up" && (
              <Badge color="info" size="xs">
                picked up
              </Badge>
            )}
          </div>
          <p className="text-[11px] text-gray-400 whitespace-nowrap">
            Approved{" "}
            <span className="text-gray-500 dark:text-gray-300">
              {request.approved_at
                ? request.approved_at.toDate().toLocaleDateString("en-AU", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                : "-"}
            </span>
          </p>
        </div>
      </div>

      {/* ── Item preview ── */}
      <div className="flex gap-3 items-center rounded-xl bg-gray-50 dark:bg-gray-800 p-3">
        {item?.assets?.[0] ? (
          <img
            src={item.assets[0].url}
            alt={item.item_name}
            className="w-14 h-14 rounded-lg object-cover shrink-0"
          />
        ) : (
          <div className="w-14 h-14 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0">
            <ImagePlaceholder />
          </div>
        )}
        <div className="flex flex-col justify-center min-w-0 gap-1">
          <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">
            {request.item_name}
          </p>
          {item && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">
                {item.item_count} remaining in stock
              </span>
              <Badge color={badge.color} size="xs">
                {badge.label}
              </Badge>
            </div>
          )}
        </div>
      </div>

      {/* ── Request info ── */}
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2">
          <p className="text-xs text-gray-400 mb-1">Requested</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">
            {request.item_count}
            <span className="text-xs font-normal text-gray-400 ml-1">
              items
            </span>
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2">
          <p className="text-xs text-gray-400 mb-1">Purpose</p>
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {request.purpose || (
              <span className="text-gray-400 font-normal">—</span>
            )}
          </p>
        </div>
      </div>

      <div className="rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2">
        <p className="text-xs text-gray-400 mb-1">Approved by</p>
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          {request.approved_by ?? "—"}
        </p>
      </div>

      {request.note && (
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2">
          <p className="text-xs text-gray-400 mb-1">Note</p>
          <p className="text-sm text-gray-900 dark:text-white">
            {request.note}
          </p>
        </div>
      )}

      {response?.status === false && (
        <Badge color="failure">{response?.message}</Badge>
      )}

      {request.status !== "picked_up" && (
        <div className="flex gap-2 mt-auto pt-2 border-t border-gray-100 dark:border-gray-700">
          <Button
            onClick={handlePickedUp}
            disabled={isUpdating}
            className="flex-1"
          >
            {isUpdating ? "Updating..." : "Mark as picked up"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AcceptedRequestDetail;
